import { useState } from "react";
import { Crown, Lock, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/auth";

/**
 * Selection-bar bulk actions for the admin CRM.
 *
 * Owns the mutations (PATCH status per row, promote-cohort-2 bulk endpoint)
 * and calls `onDone(ids, nextStatus)` so the parent can update its local
 * row state optimistically without re-fetching. The bar only renders when
 * at least one row is selected.
 *
 * `legalSigned=false` swaps the "Mark as Founder" button for a locked pill
 * so no founder gets promoted before counsel has approved.
 */
export function BulkActions({ selected, legalSigned, onDone, onClearSelection }) {
  const [busy, setBusy] = useState(false);

  const runBulk = async (next) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBusy(true);
    try {
      // One round-trip regardless of selection size. Server fans out the
      // per-row side effects (founder welcome emails, mirror network_members).
      const { data } = await api.post("/admin/waitlist/bulk", { ids, status: next });
      onDone(ids, next);
      onClearSelection();
      const ok = data?.updated ?? ids.length;
      const noop = (data?.missing_or_unchanged || []).length;
      if (noop === 0) toast.success(`${ok} lead${ok === 1 ? "" : "s"} updated`);
      else toast.success(`${ok} updated, ${noop} skipped (already ${next})`);
    } catch (err) {
      // Surface the server-side detail when we have one so 422 validation
      // errors are actionable in the CRM instead of a generic "please retry".
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" && detail
          ? `Bulk update failed: ${detail}`
          : "Bulk update failed — please retry"
      );
    } finally {
      setBusy(false);
    }
  };

  if (selected.size === 0) return null;

  return (
    <div
      data-testid="admin-bulk-bar"
      className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gold/60 bg-[#2A2724] p-4 text-white"
    >
      <span className="text-xs uppercase tracking-[0.24em] text-[#E6D3A3]">
        {selected.size} selected
      </span>
      {legalSigned ? (
        <Button
          data-testid="admin-bulk-founder"
          onClick={() => runBulk("founder")}
          disabled={busy}
          className="rounded-full bg-gold text-black hover:bg-[#AA8627]"
        >
          <Crown className="mr-2 h-4 w-4" />
          Mark as Founder
        </Button>
      ) : (
        <span
          data-testid="admin-bulk-founder-locked"
          title="Locked until a solicitor's signoff is pasted into /app/docs/legal_signoff.md"
          className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.16em] text-[#E6D3A3]"
        >
          <Lock className="h-3.5 w-3.5" />
          Legal signoff pending
        </span>
      )}
      <Button
        data-testid="admin-bulk-archive"
        onClick={() => runBulk("archived")}
        disabled={busy}
        variant="outline"
        className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
      >
        <X className="mr-2 h-4 w-4" />
        Archive
      </Button>
      <button
        type="button"
        data-testid="admin-bulk-clear"
        onClick={onClearSelection}
        className="ml-auto text-xs uppercase tracking-[0.24em] text-[#A79F94] hover:text-white"
      >
        Clear selection
      </button>
    </div>
  );
}

/**
 * Contextual "Promote all Cohort 2" button + confirmation dialog. Sits next
 * to the Cohort 2 filter chip in the toolbar; renders nothing when that
 * filter isn't active so we don't tempt fate.
 *
 * Reload is triggered via `onDone` so the parent's list refetches.
 */
export function PromoteCohort2Button({ active, onDone }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/waitlist/promote-cohort-2");
      const n = data?.promoted || 0;
      if (n === 0) toast.info("No Cohort 2 leads waiting to promote");
      else toast.success(`Promoted ${n} Cohort 2 lead${n === 1 ? "" : "s"} to Founder`);
      setConfirmOpen(false);
      onDone?.();
    } catch {
      toast.error("Bulk promote failed — check backend logs");
    } finally {
      setBusy(false);
    }
  };

  if (!active) return null;

  return (
    <>
      <button
        type="button"
        data-testid="admin-promote-cohort2"
        onClick={() => setConfirmOpen(true)}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-full border border-gold bg-gold px-3 py-1.5 text-xs font-medium text-black transition-colors hover:bg-[#AA8627] disabled:opacity-60"
      >
        <Crown className="h-3.5 w-3.5" />
        Promote all to Founder
      </button>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="admin-promote-cohort2-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Promote every Cohort 2 lead?</AlertDialogTitle>
            <AlertDialogDescription>
              This flips every open <strong>foundation_cohort_2</strong> lead to
              Founder status, mirrors them into <code>network_members</code>,
              and fires the standard Founder welcome email to each. Use this
              when you're opening the next batch of Sydney seats.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-promote-cohort2-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-promote-cohort2-confirm"
              onClick={run}
              disabled={busy}
              className="bg-gold text-black hover:bg-[#AA8627]"
            >
              {busy ? "Promoting…" : "Promote all"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
